import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SensorsIcon from '@mui/icons-material/Sensors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlaceIcon from '@mui/icons-material/Place';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import TramIcon from '@mui/icons-material/Tram';
import TrainIcon from '@mui/icons-material/Train';
import SubwayIcon from '@mui/icons-material/Subway';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import EditNoteIcon from '@mui/icons-material/EditNote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';

import { useNetworkBrowse } from '../../context/NetworkBrowseContext';
import { useFixedRoutes } from '../../context/FixedRoutesContext';
import {
  listRoutes,
  getRouteShape,
  getRouteDetail,
  type NetworkRouteListItem,
  type NetworkStop,
  type RouteVariant,
  type NetworkRouteDetail,
} from '../../services/network/networkApi';
import { findExistingFork } from '../../utils/fixedRoutes/networkRouteToFixedRoute';
import { useForkNetworkRoute } from '../../hooks/useForkNetworkRoute';
import TripsSection from './TripsSection';
import { ExportEditedNetworkModal } from './ExportEditedNetworkModal';
import { SaveVersionModal } from './SaveVersionModal';
import { generateRouteColor } from '../../utils/fixedRoutes/routeToLineConverter';
import { trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';

/**
 * Fixed Routes drawer → **Network (live)** tab (PRD-04 FR13/FR14).
 *
 * Lists ALL routes of the selected hosted network via a **server-paged** search
 * (`networkApi.listRoutes` with `q`/`limit`) — the browser never holds the whole list,
 * so a statewide feed stays cheap. Selecting a route highlights it on the map (shared
 * `NetworkBrowseContext`) and **lazily** loads its stops (`getRouteShape`) into an inline
 * list. These are read-only references — editing forks a real `FixedRoute` (FR15/FR16).
 */

const PAGE_SIZE = 300; // cap rendered rows; beyond this the user refines the search
const SEARCH_DEBOUNCE_MS = 300;

function routeTypeIcon(routeType?: number | null): React.ReactElement {
  switch (routeType) {
    case 0:
      return <TramIcon fontSize="small" />;
    case 1:
      return <SubwayIcon fontSize="small" />;
    case 2:
      return <TrainIcon fontSize="small" />;
    case 4:
      return <DirectionsBoatIcon fontSize="small" />;
    default:
      return <DirectionsBusIcon fontSize="small" />;
  }
}

interface NetworkRoutesSectionProps {
  /** Switch the drawer to the Routes tab (called after forking an editable copy). */
  onRequestRoutesTab?: () => void;
}

function NetworkRoutesSection({ onRequestRoutesTab }: NetworkRoutesSectionProps): React.ReactElement {
  const { t } = useTranslation(['mapCore']);
  const {
    feedId,
    version,
    selectedRouteId,
    setSelectedRouteId,
    setSelectedStopId,
    requestZoomToRoute,
    selectedVariant,
    setSelectedVariant,
    setPickerOpen,
  } = useNetworkBrowse();
  const { routes: fixedRoutes, setSelectedRouteId: selectFixedRoute } = useFixedRoutes();
  const forkNetworkRoute = useForkNetworkRoute();

  // Fork-on-edit (FR15/FR16) per-row transient state.
  const [forkingRouteId, setForkingRouteId] = useState<string | null>(null);
  const [forkErrorRouteId, setForkErrorRouteId] = useState<string | null>(null);

  // Save edits as a version (PRD-05c) + full edited-network export → BODS (PRD-05b).
  const [saveOpen, setSaveOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [routes, setRoutes] = useState<NetworkRouteListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);


  // Per-selection lazy stop load (one route open at a time).
  const [stopsState, setStopsState] = useState<{
    routeId: string | null;
    stops: NetworkStop[];
    loading: boolean;
    error: boolean;
  }>({ routeId: null, stops: [], loading: false, error: false });

  // Direction/shape variants + service days of the open route (FR6a / PRD-05). Service days
  // and per-direction trip counts let the trips viewer grey out empty days/directions.
  const [variantsState, setVariantsState] = useState<{
    routeId: string | null;
    variants: RouteVariant[];
    directions: NetworkRouteDetail['directions'];
    serviceDays: string[];
  }>({ routeId: null, variants: [], directions: [], serviceDays: [] });
  // Which route's STOPS accordion is open — independent of selection, so a selected
  // route can show its variants inline with the stop list collapsed.
  const [stopsOpenId, setStopsOpenId] = useState<string | null>(null);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const tmo = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tmo);
  }, [query]);

  // Reset the search + open accordions when the feed changes.
  useEffect(() => {
    setQuery('');
    setDebouncedQuery('');
    setStopsOpenId(null);
  }, [feedId]);

  // Fetch the (server-paged) route list on feed/search change.
  useEffect(() => {
    if (!feedId) {
      setRoutes([]);
      setTotal(0);
      return undefined;
    }
    const ctl = new AbortController();
    setLoading(true);
    setError(false);
    listRoutes(feedId, {
      q: debouncedQuery || undefined,
      limit: PAGE_SIZE,
      version: version ?? undefined,
      signal: ctl.signal,
    })
      .then((res) => {
        setRoutes(res.routes);
        setTotal(res.total);
      })
      .catch(() => {
        if (!ctl.signal.aborted) setError(true);
      })
      .finally(() => {
        if (!ctl.signal.aborted) setLoading(false);
      });
    return () => ctl.abort();
  }, [feedId, debouncedQuery, version]);

  const activeVariant = selectedVariant && selectedVariant.routeId === selectedRouteId ? selectedVariant : null;

  // Lazily load the selected route's stops for the chosen variant (FR14/FR6a).
  useEffect(() => {
    if (!feedId || !selectedRouteId) {
      return undefined;
    }
    const variant = selectedVariant && selectedVariant.routeId === selectedRouteId ? selectedVariant : null;
    const ctl = new AbortController();
    setStopsState({ routeId: selectedRouteId, stops: [], loading: true, error: false });
    getRouteShape(feedId, selectedRouteId, {
      direction: variant?.direction ?? undefined,
      shapeId: variant?.shapeId ?? undefined,
      version: version ?? undefined,
      signal: ctl.signal,
    })
      .then((shapes) =>
        setStopsState({ routeId: selectedRouteId, stops: shapes[0]?.stops ?? [], loading: false, error: false }),
      )
      .catch(() => {
        if (!ctl.signal.aborted) {
          setStopsState({ routeId: selectedRouteId, stops: [], loading: false, error: true });
        }
      });
    return () => ctl.abort();
  }, [feedId, selectedRouteId, version, selectedVariant]);

  // Load the open route's variants + service days (FR6a / PRD-05).
  useEffect(() => {
    if (!feedId || !selectedRouteId) {
      setVariantsState({ routeId: null, variants: [], directions: [], serviceDays: [] });
      return undefined;
    }
    const ctl = new AbortController();
    getRouteDetail(feedId, selectedRouteId, { version: version ?? undefined, signal: ctl.signal })
      .then((d) =>
        setVariantsState({
          routeId: selectedRouteId,
          variants: d.variants ?? [],
          directions: d.directions ?? [],
          serviceDays: d.serviceDays ?? [],
        }),
      )
      .catch(() => {
        if (!ctl.signal.aborted) {
          setVariantsState({ routeId: selectedRouteId, variants: [], directions: [], serviceDays: [] });
        }
      });
    return () => ctl.abort();
  }, [feedId, selectedRouteId, version]);

  const truncated = total > routes.length;
  const headerCount = useMemo(
    () => (feedId ? t('networkRoutes.countTotal', { count: total }) : ''),
    [feedId, total, t],
  );

  const handleToggleRoute = (routeId: string) => {
    if (selectedRouteId === routeId) {
      setSelectedRouteId(null);
      setSelectedStopId(null);
      setStopsOpenId(null);
    } else {
      setSelectedRouteId(routeId);
      setSelectedStopId(null);
      setStopsOpenId(routeId); // auto-open the stop list on select
      if (feedId) {
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.network_route_opened, {
          feed_id: feedId,
          route_id: routeId,
          timestamp: new Date().toISOString(),
        });
      }
    }
  };

  // Open the editable copy in the Routes tab (select it + switch tabs). RouteBuilder's
  // editor opens on the user's click in that list — the copy is created + highlighted.
  const openForkInRoutesTab = (fixedRouteId: string) => {
    selectFixedRoute(fixedRouteId);
    onRequestRoutesTab?.();
  };

  // Fork a live route into an editable FixedRoute (or open the existing copy). The live
  // reference is never mutated; the copy carries `networkOrigin` provenance (FR15/FR16).
  // The fork itself (+ analytics) lives in `useForkNetworkRoute`, shared with the map popup.
  const handleFork = async (routeId: string) => {
    if (!feedId) return;
    const existing = findExistingFork(fixedRoutes, feedId, routeId);
    if (existing) {
      // Already forked — jump straight to the copy without a spinner.
      openForkInRoutesTab(existing.id);
      return;
    }
    setForkingRouteId(routeId);
    setForkErrorRouteId(null);
    try {
      const res = await forkNetworkRoute(feedId, version, routeId);
      if (!res) {
        setForkErrorRouteId(routeId);
        return;
      }
      openForkInRoutesTab(res.route.id);
    } catch (err) {
      console.error('[NetworkRoutesSection] failed to fork network route', routeId, err);
      setForkErrorRouteId(routeId);
    } finally {
      setForkingRouteId(null);
    }
  };

  if (!feedId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
        <SensorsIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
        <Typography variant="body2">{t('networkRoutes.noFeed')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <SensorsIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t('networkRoutes.title')}
          </Typography>
          {!!total && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {headerCount}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {t('networkRoutes.subtitle')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<BookmarkAddOutlinedIcon fontSize="small" />}
            onClick={() => setSaveOpen(true)}
            sx={{ flex: 1, textTransform: 'none' }}
          >
            {t('networkRoutes.saveVersion', 'Save version')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            onClick={() => setExportOpen(true)}
            sx={{ flex: 1, textTransform: 'none' }}
          >
            {t('networkRoutes.exportEditedNetwork', 'Export → BODS')}
          </Button>
        </Box>
      </Box>

      <SaveVersionModal open={saveOpen} onClose={() => setSaveOpen(false)} onSaved={() => setPickerOpen(true)} />
      <ExportEditedNetworkModal open={exportOpen} onClose={() => setExportOpen(false)} />

      <TextField
        fullWidth
        size="small"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('networkRoutes.searchPlaceholder')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, color: 'text.secondary' }}>
          <CircularProgress size={18} />
          <Typography variant="body2">{t('networkRoutes.loading')}</Typography>
        </Box>
      )}

      {!loading && error && (
        <Typography variant="body2" color="error" sx={{ p: 2 }}>
          {t('networkRoutes.error')}
        </Typography>
      )}

      {!loading && !error && routes.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          {t('networkRoutes.empty')}
        </Typography>
      )}

      {!loading && !error && routes.length > 0 && (
        <>
          <List dense disablePadding>
            {routes.map((r) => {
              const isSelected = selectedRouteId === r.routeId;
              // Distinct per-route colour (matches the map) — feeds often brand every route
              // the same, so deriving from routeId keeps routes tellable apart.
              const color = generateRouteColor(r.routeId);
              const primary = r.routeShortName || r.routeId;
              const secondary = r.routeLongName || (r.routeShortName ? '' : '');
              const forked = feedId ? findExistingFork(fixedRoutes, feedId, r.routeId) : undefined;
              const isForking = forkingRouteId === r.routeId;
              return (
                <Box key={r.routeId} sx={{ mb: 0.5 }}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => handleToggleRoute(r.routeId)}
                    sx={{
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        flexShrink: 0,
                        bgcolor: color || 'action.selected',
                        color: color ? '#fff' : 'text.secondary',
                      }}
                    >
                      {routeTypeIcon(r.routeType)}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                        {primary}
                      </Typography>
                      {secondary && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {secondary}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title={t('networkRoutes.liveBadgeTooltip')}>
                      <Chip
                        size="small"
                        icon={<SensorsIcon />}
                        label={forked ? t('networkRoutes.copyExists') : t('networkRoutes.liveBadge')}
                        color={forked ? 'secondary' : 'default'}
                        variant="outlined"
                        sx={{ height: 22, flexShrink: 0 }}
                      />
                    </Tooltip>
                    <Tooltip title={t('networkRoutes.zoomToRoute')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          requestZoomToRoute(r.routeId);
                        }}
                        sx={{ flexShrink: 0 }}
                      >
                        <CenterFocusStrongIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={forked ? t('networkRoutes.openCopy') : t('networkRoutes.editCopy')}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isForking}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFork(r.routeId);
                          }}
                          sx={{ flexShrink: 0 }}
                        >
                          {isForking ? (
                            <CircularProgress size={16} />
                          ) : forked ? (
                            <OpenInNewIcon fontSize="small" />
                          ) : (
                            <EditNoteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('networkRoutes.toggleStops')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selectedRouteId !== r.routeId) {
                            setSelectedRouteId(r.routeId);
                            setSelectedStopId(null);
                          }
                          setStopsOpenId((prev) => (prev === r.routeId ? null : r.routeId));
                        }}
                        sx={{ flexShrink: 0 }}
                      >
                        {stopsOpenId === r.routeId ? (
                          <ExpandMoreIcon fontSize="small" color="action" />
                        ) : (
                          <ChevronRightIcon fontSize="small" color="action" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </ListItemButton>
                  {forkErrorRouteId === r.routeId && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', pl: 5.5, pt: 0.25 }}>
                      {t('networkRoutes.forkError')}
                    </Typography>
                  )}

                  {/* Variants live just under the route name, visible even with the stop
                      accordion closed (shown whenever the route is selected). */}
                  {isSelected && variantsState.routeId === r.routeId && variantsState.variants.length > 0 && (
                    <Box sx={{ pl: 5.5, pr: 1, pt: 0.5, pb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {t('networkRoutes.variantsLabel', { count: variantsState.variants.length })}
                      </Typography>
                      {variantsState.variants.length > 1 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {variantsState.variants.map((v, vi) => {
                            const isActiveVariant = activeVariant
                              ? activeVariant.direction === v.directionId &&
                                (activeVariant.shapeId ?? null) === (v.shapeId ?? null)
                              : vi === 0;
                            return (
                              <Chip
                                key={`${v.directionId}-${v.shapeId ?? vi}`}
                                size="small"
                                label={v.headsign || t('networkRoutes.directionLabel', { id: v.directionId })}
                                color={isActiveVariant ? 'primary' : 'default'}
                                variant={isActiveVariant ? 'filled' : 'outlined'}
                                onClick={() => {
                                  setSelectedVariant({
                                    routeId: r.routeId,
                                    direction: v.directionId,
                                    shapeId: v.shapeId ?? null,
                                  });
                                  if (feedId) {
                                    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.network_variant_toggled, {
                                      feed_id: feedId,
                                      route_id: r.routeId,
                                      direction: v.directionId,
                                      has_shape: v.shapeId != null,
                                      timestamp: new Date().toISOString(),
                                    });
                                  }
                                }}
                                sx={{ height: 22, maxWidth: '100%' }}
                              />
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="caption">
                          {variantsState.variants[0].headsign ||
                            t('networkRoutes.directionLabel', { id: variantsState.variants[0].directionId })}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {isSelected && feedId && (
                    <TripsSection
                      feedId={feedId}
                      routeId={r.routeId}
                      version={version}
                      directions={variantsState.routeId === r.routeId ? variantsState.directions : []}
                      serviceDays={variantsState.routeId === r.routeId ? variantsState.serviceDays : []}
                      defaultDirection={activeVariant?.direction ?? null}
                    />
                  )}

                  <Collapse in={isSelected && stopsOpenId === r.routeId} unmountOnExit>
                    <Box sx={{ pl: 2, pr: 1, py: 1 }}>
                      {stopsState.routeId === r.routeId && stopsState.loading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                          <CircularProgress size={14} />
                          <Typography variant="caption">{t('networkRoutes.stopsLoading')}</Typography>
                        </Box>
                      )}
                      {stopsState.routeId === r.routeId && stopsState.error && (
                        <Typography variant="caption" color="error">
                          {t('networkRoutes.stopsError')}
                        </Typography>
                      )}
                      {stopsState.routeId === r.routeId && !stopsState.loading && !stopsState.error && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {t('networkRoutes.stopCount', { count: stopsState.stops.length })}
                          </Typography>
                          <List dense disablePadding>
                            {stopsState.stops.map((s, idx) => (
                              <ListItemButton
                                key={`${s.stopId}-${idx}`}
                                onClick={() => setSelectedStopId(s.stopId)}
                                sx={{ py: 0.25, borderRadius: 1 }}
                              >
                                <PlaceIcon fontSize="small" color="action" sx={{ mr: 1, flexShrink: 0 }} />
                                <Typography variant="caption" noWrap>
                                  {s.stopName || s.stopId}
                                </Typography>
                              </ListItemButton>
                            ))}
                          </List>
                        </>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </List>

          {truncated && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1, textAlign: 'center' }}>
              {t('networkRoutes.showingCount', { shown: routes.length, total })}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

export default NetworkRoutesSection;
